import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

/**
 * Public unsubscribe page. The email link points here with `?token=…`,
 * we POST it server-side, then show a friendly confirmation. We
 * deliberately keep this client-rendered (no Suspense gymnastics) so
 * there's never a flash of unstyled content.
 */
export default function UnsubscribePage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState({ loading: true, ok: false, message: "" });

  useEffect(() => {
    let mounted = true;
    if (!token) {
      setState({ loading: false, ok: false, message: "Missing unsubscribe token." });
      return;
    }
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/email/unsubscribe?token=${encodeURIComponent(token)}`;
    fetch(url, { method: "GET" })
      .then(async (r) => {
        if (!mounted) return;
        if (r.ok) {
          setState({
            loading: false,
            ok: true,
            message: "You've been unsubscribed from Feel Greats automated emails.",
          });
        } else {
          setState({
            loading: false,
            ok: false,
            message: "This unsubscribe link is invalid or has expired.",
          });
        }
      })
      .catch(() => {
        if (!mounted) return;
        setState({
          loading: false,
          ok: false,
          message: "We couldn't reach the server — please try again in a moment.",
        });
      });
    return () => {
      mounted = false;
    };
  }, [token]);

  return (
    <main
      className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12 bg-emerald-50/30"
      data-testid="unsubscribe-page"
    >
      <Card className="rounded-2xl border-emerald-100 max-w-md w-full">
        <CardContent className="p-8 text-center">
          {state.loading ? (
            <div className="flex flex-col items-center gap-3 py-4" data-testid="unsubscribe-loading">
              <Loader2 className="w-7 h-7 text-emerald-700 animate-spin" />
              <p className="text-sm text-emerald-900/70">Updating your preferences…</p>
            </div>
          ) : state.ok ? (
            <div data-testid="unsubscribe-success">
              <CheckCircle2 className="w-10 h-10 text-emerald-700 mx-auto mb-3" />
              <h1 className="font-display text-2xl text-emerald-950 font-light tracking-tight">
                You're unsubscribed
              </h1>
              <p className="text-sm text-emerald-900/70 leading-relaxed mt-2">
                {state.message} You can still reach us anytime at{" "}
                <a
                  href="mailto:support@feelgreats.co.uk"
                  className="text-emerald-700 underline"
                >
                  support@feelgreats.co.uk
                </a>
                .
              </p>
              <Link to="/" className="inline-block mt-6">
                <Button className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white">
                  Back to Feel Greats
                </Button>
              </Link>
            </div>
          ) : (
            <div data-testid="unsubscribe-error">
              <AlertCircle className="w-10 h-10 text-amber-600 mx-auto mb-3" />
              <h1 className="font-display text-2xl text-emerald-950 font-light tracking-tight">
                We couldn't process that
              </h1>
              <p className="text-sm text-emerald-900/70 leading-relaxed mt-2">
                {state.message}
              </p>
              <Link to="/" className="inline-block mt-6">
                <Button variant="outline" className="rounded-full">
                  Back to Feel Greats
                </Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
    </main>
  );
}
